import { useState, useEffect, useCallback } from "react";
import { GetAdminLogs, GetAllAdmins, GetServiceAddLogs } from "../apis/get";
import LoaderService from "../../services/LoaderService";
import toast from "react-hot-toast";
import {
  FileText,
  User,
  Activity,
  Clock,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Globe,
  Code2,
  CheckCircle2,
  XCircle,
  X,
  Eye
} from "lucide-react";
import { createPortal } from "react-dom";

const PayloadModal = ({ title, data, onClose }) => {
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <h3 className="text-lg font-black text-white uppercase tracking-tight italic flex items-center gap-2">
            <Code2 className="w-5 h-5 text-blue-500" />
            {title}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 hover:text-white transition-colors"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto custom-scrollbar bg-slate-950/30">
          <pre className="text-xs font-mono text-blue-300 leading-relaxed whitespace-pre-wrap break-all bg-slate-950 p-4 rounded-xl border border-slate-800/50">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
        <div className="px-6 py-4 border-t border-slate-800 flex justify-end bg-slate-950/50">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const getServiceName = (payload) => {
  if (!payload) return "N/A";
  let data = payload;
  
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (e) {
      const match = payload.match(/"service_name"\s*:\s*"([^"]+)"/) || 
                    payload.match(/"serviceName"\s*:\s*"([^"]+)"/) ||
                    payload.match(/"service"\s*:\s*"([^"]+)"/);
      if (match && match[1]) {
        return match[1];
      }
      return "N/A";
    }
  }
  
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (e) {
      return "N/A";
    }
  }
  
  if (data && typeof data === "object") {
    return data.service_name || data.serviceName || data.service || "N/A";
  }
  
  return "N/A";
};

const LogDetailModal = ({ log, onClose }) => {
  const [activeTab, setActiveTab] = useState("request");

  const formatPayload = (payload) => {
    if (!payload) return "{}";
    if (typeof payload === "object") {
      return JSON.stringify(payload, null, 2);
    }
    try {
      const parsed = JSON.parse(payload);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return String(payload);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-black text-white uppercase tracking-wider italic flex items-center gap-2">
              <Code2 className="w-4 h-4 text-blue-500" />
              Detailed Log View
            </h3>
            <span className="text-[9px] text-slate-500 font-mono">Log ID: #{log.id}</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 hover:text-white transition-colors"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-5 bg-slate-950/30">
          <div className="bg-slate-950/80 p-4 rounded-xl border border-slate-800/80 space-y-1.5">
            <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5 text-blue-500" />
              Full Endpoint URL
            </label>
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded border border-slate-700 bg-slate-800 text-slate-300">
                {log.method}
              </span>
              <span className="text-xs font-mono text-slate-300 select-all break-all leading-relaxed">
                {log.endpoint || "N/A"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-950/50 p-4 rounded-xl border border-slate-800/50 space-y-1">
              <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Admin Details</span>
              <p className="text-xs text-slate-200 font-bold">
                {log.admin_name || log.admin_username || log.username || "N/A"} (ID: {log.admin_id || "N/A"})
              </p>
            </div>
            <div className="bg-slate-950/50 p-4 rounded-xl border border-slate-800/50 space-y-1">
              <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Event Time & Status</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-300 font-mono">
                  {new Date(log.created_at).toLocaleString()}
                </span>
                <span className="text-[9px] font-black text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded border border-blue-500/20">
                  {log.http_status || log.status}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2 p-1 bg-slate-950 border border-slate-800/60 rounded-xl w-fit">
              <button
                onClick={() => setActiveTab("request")}
                className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${
                  activeTab === "request"
                    ? "bg-blue-600 text-white"
                    : "text-slate-500 hover:text-slate-300"
                }`}
              >
                Request Payload
              </button>
              <button
                onClick={() => setActiveTab("response")}
                className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${
                  activeTab === "response"
                    ? "bg-blue-600 text-white"
                    : "text-slate-500 hover:text-slate-300"
                }`}
              >
                Response Payload
              </button>
            </div>

            <pre className="text-xs font-mono text-blue-300 leading-relaxed whitespace-pre-wrap break-all bg-slate-950 p-4 rounded-xl border border-slate-800/50 max-h-[35vh] overflow-y-auto custom-scrollbar">
              {activeTab === "request"
                ? formatPayload(log.request_payload)
                : formatPayload(log.response_payload)}
            </pre>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-800 flex justify-end bg-slate-950/50">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const AdminLogs = () => {
  const [activeTab, setActiveTab] = useState("audit");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPayload, setSelectedPayload] = useState(null);
  const [selectedDetailLog, setSelectedDetailLog] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 100,
    total: 0
  });

  const [filters, setFilters] = useState({
    admin_name: "",
    endpoint: "",
    status: "",
    service_name: "",
    start_date: "",
    end_date: ""
  });

  const [tempFilters, setTempFilters] = useState({
    admin_name: "",
    endpoint: "",
    status: "",
    service_name: "",
    start_date: "",
    end_date: ""
  });

  const [adminOptions, setAdminOptions] = useState(["mohit"]);
  const [endpointOptions, setEndpointOptions] = useState([
    "force",
    "deactivate-pair",
    "reactivate-pair",
    "set-community",
    "assign-self",
    "update-feedback",
    "update-user-instruction-assignment",
    "upload-excel-users",
    "add-credits-to-user",
    "show-admin-log"
  ]);
  const [statusOptions] = useState([
    { label: "All Statuses", value: "" },
    { label: "200 OK", value: "200" },
    { label: "201 Created", value: "201" },
    { label: "400 Bad Request", value: "400" },
    { label: "401 Unauthorized", value: "401" },
    { label: "403 Forbidden", value: "403" },
    { label: "404 Not Found", value: "404" },
    { label: "500 Internal Error", value: "500" }
  ]);

  const [serviceOptions, setServiceOptions] = useState([]);

  const [showAdminDropdown, setShowAdminDropdown] = useState(false);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const container = document.getElementById("admin-select-container");
      if (container && !container.contains(event.target)) {
        setShowAdminDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Load admin list from backend when mounting
  useEffect(() => {
    const loadAdmins = async () => {
      try {
        const response = await GetAllAdmins();
        if (response && response.status === true && Array.isArray(response.data)) {
          const names = response.data.map(admin => admin.username).filter(Boolean);
          const uniqueNames = [...new Set(names)].sort();
          setAdminOptions(uniqueNames);
        } else if (Array.isArray(response)) {
          const names = response.map(admin => admin.username).filter(Boolean);
          const uniqueNames = [...new Set(names)].sort();
          setAdminOptions(uniqueNames);
        }
      } catch (err) {
        console.error("Failed to load admin options:", err);
      }
    };
    loadAdmins();
  }, []);

  // Update dynamic filter suggestions based on logs
  useEffect(() => {
    if (logs.length > 0) {
      const logAdmins = logs.map(log => log.admin_name || log.admin_username || log.username).filter(Boolean);
      setAdminOptions(prev => [...new Set([...prev, ...logAdmins])].sort());

      const logEndpoints = logs.map(log => log.endpoint).filter(Boolean);
      setEndpointOptions(prev => [...new Set([...prev, ...logEndpoints])].sort());

      const logServices = logs
        .map(log => getServiceName(log.request_payload))
        .filter(name => name && name !== "N/A");
      setServiceOptions(prev => [...new Set([...prev, ...logServices])].sort());
    }
  }, [logs]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    LoaderService.show();
    try {
      const response = activeTab === "simplified"
        ? await GetServiceAddLogs(pagination.skip, pagination.limit, filters)
        : await GetAdminLogs(pagination.skip, pagination.limit, filters);
      console.log("Admin Logs Response:", response);

      // The user provided structure is an array directly or inside data
      if (Array.isArray(response)) {
        setLogs(response);
      } else if (response?.status === "success" || response?.status === true) {
        setLogs(response.data || []);
        if (response.count !== undefined) {
          setPagination(prev => ({ ...prev, total: response.count }));
        }
      } else if (response?.data && Array.isArray(response.data)) {
        setLogs(response.data);
      } else {
        setLogs([]);
      }
    } catch (error) {
      console.error("Failed to load admin logs:", error);
      toast.error("Failed to load admin logs");
      setLogs([]);
    } finally {
      setLoading(false);
      LoaderService.hide();
    }
  }, [pagination.skip, pagination.limit, filters, activeTab]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Automatically update filters with a 500ms debounce when user types
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        admin_name: tempFilters.admin_name,
        endpoint: tempFilters.endpoint,
        status: tempFilters.status,
        service_name: tempFilters.service_name,
        start_date: tempFilters.start_date,
        end_date: tempFilters.end_date
      }));
      setPagination(prev => ({ ...prev, skip: 0 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [
    tempFilters.admin_name,
    tempFilters.endpoint,
    tempFilters.status,
    tempFilters.service_name,
    tempFilters.start_date,
    tempFilters.end_date
  ]);

  const handleTempFilterChange = (key, value) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const handleResetFilters = () => {
    const cleared = {
      admin_name: "",
      endpoint: "",
      status: "",
      service_name: "",
      start_date: "",
      end_date: ""
    };
    setTempFilters(cleared);
    setFilters(cleared);
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const handlePrevPage = () => {
    if (pagination.skip >= pagination.limit) {
      setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }));
    }
  };

  const handleNextPage = () => {
    if (logs.length >= pagination.limit) {
      setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateString;
    }
  };

  const getEndpointLastName = (endpoint) => {
    if (!endpoint) return "N/A";
    const withoutQueryParams = endpoint.split("?")[0];
    const trimmed = withoutQueryParams.replace(/\/+$/, "");
    const parts = trimmed.split("/");
    const lastPart = parts[parts.length - 1];
    return lastPart || endpoint;
  };


  const getStatusBadge = (status, httpStatus) => {
    const isSuccess = status === "success" || httpStatus === 200;
    return (
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[13px] font-black uppercase tracking-wider ${isSuccess
          ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
          : "bg-red-500/10 text-red-400 border-red-500/20"
        }`}>
        {isSuccess ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
        {httpStatus || status}
      </div>
    );
  };

  const getMethodColor = (method) => {
    switch (method?.toUpperCase()) {
      case "GET": return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
      case "POST": return "text-blue-400 bg-blue-500/10 border-blue-500/20";
      case "PATCH": return "text-amber-400 bg-amber-500/10 border-amber-500/20";
      case "PUT": return "text-indigo-400 bg-indigo-500/10 border-indigo-500/20";
      case "DELETE": return "text-red-400 bg-red-500/10 border-red-500/20";
      default: return "text-slate-400 bg-slate-500/10 border-slate-500/20";
    }
  };

  const filteredLogs = logs.filter((log) => {
    if (log.endpoint && log.endpoint.toLowerCase().includes("match-making-astrology-details")) {
      return false;
    }
    const term = searchTerm.toLowerCase();
    const adminIdMatch = log.admin_id?.toString().toLowerCase().includes(term);
    const adminNameMatch = (log.admin_name || log.admin_username || log.username || "").toLowerCase().includes(term);
    const logIdMatch = log.id?.toString().toLowerCase().includes(term);
    return adminIdMatch || adminNameMatch || logIdMatch;
  });

  return (
    <div className="mt-12 space-y-6 animate-fade-in p-4 sm:p-6 lg:p-8">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
          <div className="relative flex items-center gap-4 bg-slate-950 px-6 py-3 rounded-xl border border-slate-800">
            <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <FileText className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-white tracking-tight uppercase italic flex items-center gap-2">
                Admin Audit Logs
              </h1>
              <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.2em] mt-0.5">
                Real-time API Event Monitoring
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
          <div className="relative w-full sm:w-64 group">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <Search className="w-4 h-4 text-slate-500 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              type="text"
              placeholder="Search by ID or Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-10 py-2.5 bg-slate-900/50 border border-slate-800 text-slate-200 text-xs font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all placeholder:text-slate-600 placeholder:font-black placeholder:uppercase placeholder:tracking-widest"
            />
            {searchTerm && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="p-1 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          {activeTab === "simplified" && (
            <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 px-3.5 py-2.5 rounded-xl">
              <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Limit:</label>
              <select
                value={pagination.limit}
                onChange={(e) => {
                  const newLimit = Number(e.target.value);
                  setPagination(prev => ({ ...prev, limit: newLimit, skip: 0 }));
                }}
                className="bg-transparent text-slate-300 text-xs font-bold outline-none cursor-pointer"
              >
                <option value={25} className="bg-slate-950">25</option>
                <option value={50} className="bg-slate-950">50</option>
                <option value={100} className="bg-slate-950">100</option>
                <option value={200} className="bg-slate-950">200</option>
                <option value={500} className="bg-slate-950">500</option>
              </select>
            </div>
          )}

          <button
            onClick={fetchLogs}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white rounded-xl border border-slate-800 transition-all active:scale-95 disabled:opacity-50 group whitespace-nowrap"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
            <span className="text-xs font-black uppercase tracking-wider">Refresh</span>
          </button>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex items-center gap-2 p-1 bg-slate-950 border border-slate-800/80 rounded-2xl w-fit">
        <button
          onClick={() => setActiveTab("audit")}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
            activeTab === "audit"
              ? "bg-blue-600 text-white shadow-lg shadow-blue-600/10"
              : "text-slate-500 hover:text-slate-300"
          }`}
        >
          <FileText className="w-4 h-4" />
          Audit Logs
        </button>
        <button
          onClick={() => setActiveTab("simplified")}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
            activeTab === "simplified"
              ? "bg-blue-600 text-white shadow-lg shadow-blue-600/10"
              : "text-slate-500 hover:text-slate-300"
          }`}
        >
          <Activity className="w-4 h-4" />
          Simplified Service Logs
        </button>
      </div>

      {/* Filters Section */}
      <div className="relative z-30 bg-slate-900/40 border border-slate-800/60 rounded-[2rem] p-6 backdrop-blur-md shadow-2xl space-y-4">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2 text-white font-black uppercase tracking-wider text-xs italic">
            <Activity className="w-4 h-4 text-blue-500" />
            Filter Options (Server-Side)
          </div>
          {(filters.admin_name || filters.endpoint || filters.status || filters.service_name || filters.start_date || filters.end_date) && (
            <span className="text-[9px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded-full font-black uppercase tracking-wider">
              Active Filters
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Admin Name Filter */}
          <div className="flex flex-col gap-1.5 relative" id="admin-select-container">
            <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Admin Username</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Select or type admin..."
                value={tempFilters.admin_name}
                onFocus={() => setShowAdminDropdown(true)}
                onChange={(e) => handleTempFilterChange("admin_name", e.target.value)}
                className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-xs font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all pr-16"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center gap-1.5">
                {tempFilters.admin_name && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTempFilterChange("admin_name", "");
                      setShowAdminDropdown(false);
                    }}
                    className="p-1 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
                <ChevronDown className={`w-4 h-4 text-slate-500 pointer-events-none transition-transform duration-300 ${showAdminDropdown ? 'rotate-180' : ''}`} />
              </div>
            </div>

            {/* Custom Searchable Dropdown menu */}
            {showAdminDropdown && (
              <div className="absolute top-[calc(100%+4px)] left-0 w-full z-50 max-h-60 overflow-y-auto custom-scrollbar bg-slate-950/95 border border-slate-800 rounded-2xl shadow-2xl p-2 animate-in fade-in duration-200 backdrop-blur-md">
                {adminOptions.filter(name =>
                  name.toLowerCase().includes(tempFilters.admin_name.toLowerCase())
                ).length > 0 ? (
                  adminOptions
                    .filter(name => name.toLowerCase().includes(tempFilters.admin_name.toLowerCase()))
                    .map((name) => (
                      <button
                        key={name}
                        onClick={() => {
                          handleTempFilterChange("admin_name", name);
                          setShowAdminDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2.5 text-xs font-bold text-slate-300 hover:text-white hover:bg-blue-600/20 rounded-xl transition-all flex items-center justify-between"
                      >
                        <span>{name}</span>
                        {tempFilters.admin_name === name && (
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                        )}
                      </button>
                    ))
                ) : (
                  <div className="px-4 py-3 text-xs text-slate-500 italic text-center">
                    No admins found
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Endpoint Filter or Service Name Filter */}
          {activeTab === "audit" ? (
            <div className="flex flex-col gap-1.5">
              <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Endpoint</label>
              <input
                type="text"
                placeholder="Search endpoint (e.g. force)..."
                value={tempFilters.endpoint}
                onChange={(e) => handleTempFilterChange("endpoint", e.target.value)}
                className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-xs font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
              />
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Service Name</label>
              <select
                value={tempFilters.service_name}
                onChange={(e) => {
                  const val = e.target.value;
                  handleTempFilterChange("service_name", val);
                  setFilters(prev => ({ ...prev, service_name: val }));
                  setPagination(prev => ({ ...prev, skip: 0 }));
                }}
                className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-xs font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-slate-300 select-dropdown"
              >
                <option value="" className="bg-slate-950">All Services</option>
                {serviceOptions.map(opt => (
                  <option key={opt} value={opt} className="bg-slate-950">
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Status Filter */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">HTTP / Response Status</label>
            <select
              value={tempFilters.status}
              onChange={(e) => {
                const val = e.target.value;
                handleTempFilterChange("status", val);
                setFilters(prev => ({ ...prev, status: val }));
                setPagination(prev => ({ ...prev, skip: 0 }));
              }}
              className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-xs font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-slate-300"
            >
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value} className="bg-slate-950">
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Time Range Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Start Date & Time</label>
            <input
              type="datetime-local"
              value={tempFilters.start_date}
              onChange={(e) => handleTempFilterChange("start_date", e.target.value)}
              className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-xs font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-slate-300"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">End Date & Time</label>
            <input
              type="datetime-local"
              value={tempFilters.end_date}
              onChange={(e) => handleTempFilterChange("end_date", e.target.value)}
              className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-xs font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-slate-300"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-800/60">
          <div className="flex items-center gap-2">
            <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Limit:</label>
            <select
              value={pagination.limit}
              onChange={(e) => {
                const newLimit = Number(e.target.value);
                setPagination(prev => ({ ...prev, limit: newLimit, skip: 0 }));
              }}
              className="px-2.5 py-1 bg-slate-950 border border-slate-800 text-slate-300 text-xs font-bold rounded-lg focus:ring-1 focus:ring-blue-500 outline-none"
            >
              <option value={25} className="bg-slate-950">25</option>
              <option value={50} className="bg-slate-950">50</option>
              <option value={100} className="bg-slate-950">100</option>
              <option value={200} className="bg-slate-950">200</option>
              <option value={500} className="bg-slate-950">500</option>
            </select>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 bg-slate-950 hover:bg-slate-800 text-slate-400 hover:text-white border border-slate-800/80 rounded-xl transition-all text-[10px] font-black uppercase tracking-wider"
            >
              Reset
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl transition-all text-[10px] font-black uppercase tracking-wider shadow-lg shadow-blue-500/10 hover:shadow-blue-500/20 active:scale-95"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-slate-900/40 border border-slate-800/60 rounded-[2rem] overflow-hidden backdrop-blur-md shadow-2xl relative">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/[0.02] to-transparent pointer-events-none"></div>

        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse relative">
            <thead>
              <tr className="border-b border-slate-800/80 bg-slate-950/40">
                {activeTab === "audit" ? (
                  <>
                    <th className="px-6 py-5 text-[14px] font-black text-slate-500 uppercase tracking-[0.2em]">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Time
                      </div>
                    </th>
                    <th className="px-6 py-5 text-[14px] font-black text-slate-500 uppercase tracking-[0.2em]">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Admin ID
                      </div>
                    </th>
                    <th className="px-6 py-5 text-[14px] font-black text-slate-500 uppercase tracking-[0.2em]">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Admin Name
                      </div>
                    </th>
                    <th className="px-6 py-5 text-[14px] font-black text-slate-500 uppercase tracking-[0.2em]">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Endpoint
                      </div>
                    </th>
                    <th className="px-6 py-5 text-[14px] font-black text-slate-500 uppercase tracking-[0.2em]">
                      <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4" />
                        Event / Status
                      </div>
                    </th>
                    <th className="px-6 py-5 text-[14px] font-black text-slate-500 uppercase tracking-[0.2em]">
                      Payloads
                    </th>
                  </>
                ) : (
                  <>
                    <th className="px-6 py-5 text-[14px] font-black text-slate-500 uppercase tracking-[0.2em]">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Time
                      </div>
                    </th>
                    <th className="px-6 py-5 text-[14px] font-black text-slate-500 uppercase tracking-[0.2em]">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Admin ID
                      </div>
                    </th>
                    <th className="px-6 py-5 text-[14px] font-black text-slate-500 uppercase tracking-[0.2em]">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Admin Name
                      </div>
                    </th>
                    <th className="px-6 py-5 text-[14px] font-black text-slate-500 uppercase tracking-[0.2em]">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Endpoint Last Name
                      </div>
                    </th>
                    <th className="px-6 py-5 text-[14px] font-black text-slate-500 uppercase tracking-[0.2em]">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Service Name
                      </div>
                    </th>
                    <th className="px-6 py-5 text-[14px] font-black text-slate-500 uppercase tracking-[0.2em]">
                      <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4" />
                        HTTP Status
                      </div>
                    </th>
                    <th className="px-6 py-5 text-[14px] font-black text-slate-500 uppercase tracking-[0.2em]">
                      Actions
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40">
              {filteredLogs.length > 0 ? (
                filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-blue-500/[0.02] transition-colors group">
                    {activeTab === "audit" ? (
                      <>
                        <td className="px-6 py-5">
                          <div className="text-[15px] text-slate-400 font-mono tracking-tight">
                            {formatDate(log.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-base font-bold text-slate-200 group-hover:text-white transition-colors">
                              {log.admin_id}
                            </span>
                            <span className="text-[13px] text-slate-500 font-black uppercase tracking-tighter">
                              ID: #{log.id}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-base font-bold text-slate-200 group-hover:text-white transition-colors">
                              {log.admin_name || log.admin_username || log.username || "N/A"}
                            </span>
                            <span className="text-[13px] text-slate-500 font-black uppercase tracking-tighter">
                              Admin
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex flex-col gap-1.5 max-w-md">
                            <span className={`inline-flex items-center w-fit px-1.5 py-0.5 rounded border text-[13px] font-black ${getMethodColor(log.method)}`}>
                              {log.method}
                            </span>
                            <span className="text-[15px] text-slate-400 font-mono break-all hover:text-blue-400 transition-colors cursor-help" title={log.endpoint}>
                              {log.endpoint}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex flex-col gap-2">
                            <div className="text-[14px] font-black text-slate-300 uppercase tracking-wider">
                              {log.event_type?.replace(/_/g, ' ')}
                            </div>
                            {getStatusBadge(log.status, log.http_status)}
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedPayload({ title: "Request Payload", data: log.request_payload })}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-blue-600/20 text-slate-400 hover:text-blue-400 rounded-lg border border-slate-700 hover:border-blue-500/30 transition-all text-[13px] font-black uppercase tracking-widest active:scale-95"
                            >
                              <Eye className="w-4 h-4" />
                              Req
                            </button>
                            <button
                              onClick={() => setSelectedPayload({ title: "Response Payload", data: log.response_payload })}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-emerald-600/20 text-slate-400 hover:text-emerald-400 rounded-lg border border-slate-700 hover:border-emerald-500/30 transition-all text-[13px] font-black uppercase tracking-widest active:scale-95"
                            >
                              <Eye className="w-4 h-4" />
                              Res
                            </button>
                            <button
                              onClick={() => setSelectedDetailLog(log)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg border border-blue-500/20 transition-all text-[13px] font-black uppercase tracking-widest active:scale-95"
                            >
                              <Eye className="w-4 h-4" />
                              Detail
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-6 py-5">
                          <div className="text-[15px] text-slate-400 font-mono tracking-tight">
                            {formatDate(log.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <span className="text-base font-bold text-slate-200 group-hover:text-white transition-colors">
                            {log.admin_id}
                          </span>
                        </td>
                        <td className="px-6 py-5">
                          <span className="text-base font-bold text-slate-200 group-hover:text-white transition-colors">
                            {log.admin_name || log.admin_username || log.username || "N/A"}
                          </span>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex flex-col gap-1.5 max-w-md">
                            <span className={`inline-flex items-center w-fit px-1.5 py-0.5 rounded border text-[13px] font-black ${getMethodColor(log.method)}`}>
                              {log.method}
                            </span>
                            <span className="text-base font-bold text-slate-300 hover:text-blue-400 transition-colors cursor-help" title={log.endpoint}>
                              {getEndpointLastName(log.endpoint)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <span className="text-base font-bold text-slate-300">
                            {getServiceName(log.request_payload)}
                          </span>
                        </td>
                        <td className="px-6 py-5">
                          {getStatusBadge(log.status, log.http_status)}
                        </td>
                        <td className="px-6 py-5">
                          <button
                            onClick={() => setSelectedDetailLog(log)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-blue-600/20 text-slate-400 hover:text-blue-400 rounded-lg border border-slate-700 hover:border-blue-500/30 transition-all text-[13px] font-black uppercase tracking-widest active:scale-95 whitespace-nowrap"
                          >
                            <Eye className="w-4 h-4" />
                            Detailed View
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={activeTab === "audit" ? 6 : 7} className="px-6 py-24 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-slate-800/50 flex items-center justify-center border border-slate-700/50">
                        <Search className="w-8 h-8 text-slate-600" />
                      </div>
                      <div>
                        <p className="text-slate-400 font-bold uppercase tracking-widest text-sm">No activity logs found</p>
                        <p className="text-slate-600 text-[10px] font-medium uppercase tracking-[0.2em] mt-1">Logs will appear here as admins perform actions</p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer / Pagination */}
        <div className="px-6 py-4 border-t border-slate-800/60 bg-slate-950/20 flex items-center justify-between">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
            <span>Showing {filteredLogs.length} entries {searchTerm && `(filtered from ${logs.length})`}</span>
            <span className="text-slate-700">|</span>
            <span className="text-slate-400">Offset: {pagination.skip}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevPage}
              disabled={pagination.skip === 0}
              className="p-1.5 rounded-lg border border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={handleNextPage}
              disabled={logs.length < pagination.limit}
              className="p-1.5 rounded-lg border border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Payload Modal */}
      {selectedPayload && (
        <PayloadModal
          title={selectedPayload.title}
          data={selectedPayload.data}
          onClose={() => setSelectedPayload(null)}
        />
      )}

      {/* Detailed Log Modal */}
      {selectedDetailLog && (
        <LogDetailModal
          log={selectedDetailLog}
          onClose={() => setSelectedDetailLog(null)}
        />
      )}
    </div>
  );
};

export default AdminLogs;
